import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { formatDistanceToNow } from "date-fns";
import {
  Copy,
  Link2,
  Link2Off,
  MoreHorizontal,
  Pencil,
  Plus,
  Power,
  PowerOff,
  RefreshCw,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { usePageHeader } from "@/hooks/usePageHeader";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useDeleteManualSim,
  useImeiModels,
  usePendingSimIds,
  useSimCards,
  useSimProfiles,
  useSyncSims,
  useUnassignSims,
} from "@/hooks/useSims";
import {
  DEFAULT_SIM_FILTERS,
  PROVIDER_LABELS,
  STATUS_LABELS,
  currentMonthUsageMb,
  filterSims,
  formatDataMb,
  imeiTac,
  profileNetworkIn,
  type SimCard,
  type SimFilters,
  type SimProvider,
  type SimStatusKind,
} from "@/lib/sims";
import {
  IccidText,
  ProviderBadge,
  SimNoteLine,
  SimStatusBadge,
} from "@/components/sims/SimBadges";
import { SimActionDialog } from "@/components/sims/SimActionDialog";
import { SimDocsPanel } from "@/components/sims/SimDocsPanel";
import { AssignSimsDialog } from "@/components/sims/AssignSimsDialog";
import { ManualSimDialog } from "@/components/sims/ManualSimDialog";
import { SimBatchesPanel } from "@/components/sims/SimBatchesPanel";
import { SimUsageOverview } from "@/components/sims/SimUsageOverview";
import { DeviceSimDialog } from "@/components/sims/DeviceSimDialog";
import { SimDetailDialog } from "@/components/sims/SimDetailDialog";
import { SimProfilesPanel } from "@/components/sims/SimProfilesPanel";
import { SetSimProfileDialog } from "@/components/sims/SetSimProfileDialog";
import {
  SimNetworkCell,
  SimNetworkHeaderLabel,
  SimNetworkCountrySelect,
  SimProfileCell,
  useSelectedNetworkCountry,
} from "@/components/sims/SimNetworkCountry";

const PAGE_SIZE = 100;
const NO_SIMS: SimCard[] = [];

const STATUS_FILTERS: SimStatusKind[] = [
  "active",
  "idle",
  "suspended",
  "deactivated",
  "inventory",
  "missing",
];

export default function SimCardsPage() {
  usePageHeader({ title: "SIM Cards" });
  const { role } = useAuth();
  const { data: sims, isLoading, error } = useSimCards();
  const { data: profiles } = useSimProfiles();
  const country = useSelectedNetworkCountry();
  const profilesById = useMemo(
    () => new Map(profiles.map((p) => [p.id, p])),
    [profiles],
  );
  const pending = usePendingSimIds();
  const unassign = useUnassignSims();
  const deleteManual = useDeleteManualSim();
  const sync = useSyncSims();

  const [filters, setFilters] = useState<SimFilters>(DEFAULT_SIM_FILTERS);
  const [selected, setSelected] = useState<string[]>([]);
  const [action, setAction] = useState<"activate" | "deactivate" | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [tab, setTab] = useState("sims");
  const [manualSim, setManualSim] = useState<SimCard | null | undefined>(
    undefined,
  );
  const [detailSimId, setDetailSimId] = useState<string | null>(null);
  const [device, setDevice] = useState<{ id: string; name: string } | null>(
    null,
  );

  // Typing stays responsive; the table catches up at lower priority.
  const deferredFilters = useDeferredValue(filters);
  const filtered = useMemo(
    () => filterSims(sims, deferredFilters),
    [sims, deferredFilters],
  );
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageSims = useMemo(
    () =>
      filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage],
  );
  useEffect(() => setPage(1), [deferredFilters]);
  const modemsByTac = useImeiModels(pageSims.map((sim) => sim.imei));

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const selectedSims = useMemo(() => {
    const byId = new Map(sims.map((s) => [s.id, s]));
    return selected.map((id) => byId.get(id)).filter((s): s is SimCard => !!s);
  }, [selected, sims]);
  const devices = useMemo(() => {
    const map = new Map<string, string>();
    for (const sim of sims) {
      if (sim.equipmentInstanceID && sim.deviceName) {
        map.set(sim.equipmentInstanceID, sim.deviceName);
      }
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [sims]);
  const lastSync = useMemo(
    () =>
      sims.reduce<number | null>(
        (max, s) =>
          s.lastSyncedAt && (!max || s.lastSyncedAt > max)
            ? s.lastSyncedAt
            : max,
        null,
      ),
    [sims],
  );

  const allVisibleSelected =
    filtered.length > 0 && filtered.every((s) => selectedSet.has(s.id));
  const toggleAll = () => {
    const filteredIds = new Set(filtered.map((s) => s.id));
    setSelected(
      allVisibleSelected
        ? selected.filter((id) => !filteredIds.has(id))
        : Array.from(new Set([...selected, ...filteredIds])),
    );
  };
  const toggleOne = useCallback(
    (id: string) =>
      setSelected((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      ),
    [],
  );
  const openDevice = useCallback(
    (id: string, name: string) => setDevice({ id, name }),
    [],
  );
  const { mutate: deleteManualSim } = deleteManual;

  // Stable props so the memoised dialogs skip re-renders while closed.
  const actionTarget = useMemo(
    () => (action ? { kind: "sims" as const, simIds: selected } : null),
    [action, selected],
  );
  const closeAction = useCallback(
    (open: boolean) => !open && setAction(null),
    [],
  );
  const clearSelection = useCallback(() => setSelected([]), []);
  const closeManual = useCallback(
    (open: boolean) => !open && setManualSim(undefined),
    [],
  );
  const closeDetail = useCallback(
    (open: boolean) => !open && setDetailSimId(null),
    [],
  );
  const closeDevice = useCallback(
    (open: boolean) => !open && setDevice(null),
    [],
  );
  const setFilter = <K extends keyof SimFilters>(
    key: K,
    value: SimFilters[K],
  ) => setFilters((prev) => ({ ...prev, [key]: value }));
  const showProfileSims = useCallback((profileId: string) => {
    setFilters({ ...DEFAULT_SIM_FILTERS, profile: profileId });
    setTab("sims");
  }, []);

  return (
    <div className="space-y-4" data-testid="sim-cards-page">
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="sims" data-testid="sim-cards-tab">
              SIM cards
            </TabsTrigger>
            <TabsTrigger value="usage" data-testid="sim-usage-tab">
              Usage
            </TabsTrigger>
            <TabsTrigger value="profiles" data-testid="sim-profiles-tab">
              Profiles
            </TabsTrigger>
            <TabsTrigger value="docs" data-testid="sim-docs-tab">
              Docs
            </TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {lastSync
                ? `Synced ${formatDistanceToNow(new Date(lastSync), { addSuffix: true })}`
                : "Not synced yet"}
            </span>
            {role === "admin" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => sync.mutate()}
                disabled={sync.isPending}
                data-testid="sync-sims"
              >
                <RefreshCw
                  className={`mr-1.5 h-4 w-4 ${sync.isPending ? "animate-spin" : ""}`}
                />
                Sync now
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => setManualSim(null)}
              data-testid="add-kpn-sim"
            >
              <Plus className="mr-1.5 h-4 w-4" /> Add KPN SIM
            </Button>
          </div>
        </div>

        <TabsContent value="sims" className="mt-4">
          <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-w-0 space-y-3">
              <div className="flex flex-wrap gap-2">
                <Input
                  placeholder="Search ICCID, MSISDN, IMSI, IMEI, device, label, note…"
                  value={filters.search}
                  onChange={(e) => setFilter("search", e.target.value)}
                  className="w-full sm:w-80"
                  data-testid="sim-search"
                />
                <Select
                  value={filters.provider}
                  onValueChange={(v) =>
                    setFilter("provider", v as SimProvider | "all")
                  }
                >
                  <SelectTrigger
                    className="w-36"
                    data-testid="sim-provider-filter"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All providers</SelectItem>
                    {(Object.keys(PROVIDER_LABELS) as SimProvider[]).map(
                      (p) => (
                        <SelectItem key={p} value={p}>
                          {PROVIDER_LABELS[p]}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <Select
                  value={filters.status}
                  onValueChange={(v) =>
                    setFilter("status", v as SimStatusKind | "all")
                  }
                >
                  <SelectTrigger
                    className="w-36"
                    data-testid="sim-status-filter"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {STATUS_FILTERS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={filters.assignment}
                  onValueChange={(v) =>
                    setFilter("assignment", v as SimFilters["assignment"])
                  }
                >
                  <SelectTrigger
                    className="w-36"
                    data-testid="sim-assignment-filter"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Assigned or not</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filters.deviceID}
                  onValueChange={(v) => setFilter("deviceID", v)}
                >
                  <SelectTrigger
                    className="w-44"
                    data-testid="sim-device-filter"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All devices</SelectItem>
                    {devices.map(([id, name]) => (
                      <SelectItem key={id} value={id}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={filters.profile}
                  onValueChange={(v) => setFilter("profile", v)}
                >
                  <SelectTrigger
                    className="w-44"
                    data-testid="sim-profile-filter"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All profiles</SelectItem>
                    <SelectItem value="none">No profile</SelectItem>
                    {profiles.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {PROVIDER_LABELS[p.provider]} · {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <SimNetworkCountrySelect />
              </div>

              {selected.length > 0 && (
                <div
                  className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 p-2"
                  data-testid="sim-batch-bar"
                >
                  <span className="px-1 text-sm font-medium">
                    {selected.length} selected
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAction("activate")}
                    data-testid="batch-activate"
                  >
                    <Power className="mr-1.5 h-4 w-4" /> Activate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAction("deactivate")}
                    data-testid="batch-deactivate"
                  >
                    <PowerOff className="mr-1.5 h-4 w-4" /> Deactivate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAssignOpen(true)}
                    data-testid="batch-assign"
                  >
                    <Link2 className="mr-1.5 h-4 w-4" /> Assign to device…
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={
                      unassign.isPending ||
                      !selectedSims.some((s) => s.equipmentInstanceID)
                    }
                    onClick={() =>
                      unassign.mutate(
                        selectedSims
                          .filter((s) => s.equipmentInstanceID)
                          .map((s) => s.id),
                        { onSuccess: () => setSelected([]) },
                      )
                    }
                    data-testid="batch-unassign"
                  >
                    <Link2Off className="mr-1.5 h-4 w-4" /> Unassign
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setProfileOpen(true)}
                    data-testid="batch-set-profile"
                  >
                    <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Set
                    profile…
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelected([])}
                  >
                    <X className="mr-1 h-4 w-4" /> Clear
                  </Button>
                </div>
              )}

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allVisibleSelected}
                          onCheckedChange={toggleAll}
                          aria-label="Select all matching SIMs"
                          data-testid="sim-select-all"
                        />
                      </TableHead>
                      <TableHead>ICCID</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Profile</TableHead>
                      {country && (
                        <TableHead
                          className="whitespace-nowrap"
                          data-testid="sim-network-header"
                        >
                          <SimNetworkHeaderLabel name={country.name} />
                        </TableHead>
                      )}
                      <TableHead>Device</TableHead>
                      <TableHead className="whitespace-nowrap text-right">
                        This month
                      </TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading && (
                      <TableRow>
                        <TableCell
                          colSpan={country ? 9 : 8}
                          className="py-8 text-center text-muted-foreground"
                        >
                          Loading SIM cards…
                        </TableCell>
                      </TableRow>
                    )}
                    {error && (
                      <TableRow>
                        <TableCell
                          colSpan={country ? 9 : 8}
                          className="py-8 text-center text-destructive"
                        >
                          {error.message}
                        </TableCell>
                      </TableRow>
                    )}
                    {!isLoading &&
                      pageSims.map((sim) => (
                        <SimRow
                          key={sim.id}
                          sim={sim}
                          selected={selectedSet.has(sim.id)}
                          pending={pending.has(sim.id)}
                          network={
                            country
                              ? profileNetworkIn(
                                  sim.profileID
                                    ? profilesById.get(sim.profileID)
                                    : null,
                                  country.code,
                                )
                              : undefined
                          }
                          modem={
                            modemsByTac.get(imeiTac(sim.imei) ?? "")?.name ??
                            null
                          }
                          onToggle={toggleOne}
                          onOpenDevice={openDevice}
                          onOpenDetail={setDetailSimId}
                          onEditManual={setManualSim}
                          onDeleteManual={deleteManualSim}
                        />
                      ))}
                    {!isLoading && !error && filtered.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={country ? 9 : 8}
                          className="py-8 text-center text-muted-foreground"
                        >
                          No SIM cards match the filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="sim-count"
                >
                  {filtered.length} of {sims.length} SIM cards
                  {totalPages > 1 &&
                    ` · showing ${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(
                      currentPage * PAGE_SIZE,
                      filtered.length,
                    )}`}
                </p>
                {totalPages > 1 && (
                  <SimPagination
                    page={currentPage}
                    totalPages={totalPages}
                    onChange={setPage}
                  />
                )}
              </div>
            </div>
            <SimBatchesPanel />
          </div>
        </TabsContent>

        <TabsContent value="usage" className="mt-4">
          <SimUsageOverview sims={sims} onOpenDevice={setDevice} />
        </TabsContent>

        <TabsContent value="profiles" className="mt-4">
          <SimProfilesPanel sims={sims} onShowSims={showProfileSims} />
        </TabsContent>

        <TabsContent value="docs" className="mt-4">
          <SimDocsPanel />
        </TabsContent>
      </Tabs>

      <SimActionDialog
        action={action}
        target={actionTarget}
        onOpenChange={closeAction}
      />
      <AssignSimsDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        sims={assignOpen ? selectedSims : NO_SIMS}
        allSims={assignOpen ? sims : NO_SIMS}
        onDone={clearSelection}
      />
      <SetSimProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        sims={profileOpen ? selectedSims : NO_SIMS}
        onDone={clearSelection}
      />
      <ManualSimDialog
        open={manualSim !== undefined}
        onOpenChange={closeManual}
        sim={manualSim}
      />
      <DeviceSimDialog instance={device} onOpenChange={closeDevice} />
      <SimDetailDialog simId={detailSimId} onOpenChange={closeDetail} />
    </div>
  );
}

type SimRowProps = {
  sim: SimCard;
  selected: boolean;
  pending: boolean;
  /** Network in the selected country; undefined hides the column. */
  network: string | null | undefined;
  /** Modem model the network last saw the SIM in, if known. */
  modem: string | null;
  onToggle: (id: string) => void;
  onOpenDevice: (id: string, name: string) => void;
  onOpenDetail: (id: string) => void;
  onEditManual: (sim: SimCard) => void;
  onDeleteManual: (id: string) => void;
};

/**
 * Zero hands out fresh row objects on every change, so compare SIM fields
 * shallowly instead of by identity; only rows whose data changed re-render.
 */
function simRowPropsEqual(prev: SimRowProps, next: SimRowProps) {
  if (
    prev.selected !== next.selected ||
    prev.pending !== next.pending ||
    prev.network !== next.network ||
    prev.modem !== next.modem ||
    prev.onToggle !== next.onToggle ||
    prev.onOpenDevice !== next.onOpenDevice ||
    prev.onOpenDetail !== next.onOpenDetail ||
    prev.onEditManual !== next.onEditManual ||
    prev.onDeleteManual !== next.onDeleteManual
  )
    return false;
  if (prev.sim === next.sim) return true;
  const keys = Object.keys(next.sim) as (keyof SimCard)[];
  return keys.every((key) => prev.sim[key] === next.sim[key]);
}

const SimRow = memo(function SimRow({
  sim,
  selected,
  pending,
  network,
  modem,
  onToggle,
  onOpenDevice,
  onOpenDetail,
  onEditManual,
  onDeleteManual,
}: SimRowProps) {
  return (
    <TableRow
      data-testid={`sim-row-${sim.iccid}`}
      data-state={selected ? "selected" : undefined}
    >
      <TableCell>
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggle(sim.id)}
          aria-label={`Select SIM ${sim.iccid}`}
          data-testid={`sim-select-${sim.iccid}`}
        />
      </TableCell>
      <TableCell>
        <span className="inline-flex items-center gap-1">
          <button
            type="button"
            className="font-mono text-xs hover:underline"
            title={sim.iccid}
            onClick={() => onOpenDetail(sim.id)}
            data-testid={`sim-open-${sim.iccid}`}
          >
            <IccidText iccid={sim.iccid} />
          </button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => copyIccid(sim.iccid)}
            aria-label="Copy ICCID"
          >
            <Copy className="h-3 w-3" />
          </Button>
        </span>
        {sim.label && (
          <div className="text-xs text-muted-foreground">{sim.label}</div>
        )}
        <SimNoteLine note={sim.note} />
      </TableCell>
      <TableCell>
        <ProviderBadge provider={sim.provider} />
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <SimStatusBadge sim={sim} pending={pending} />
      </TableCell>
      <SimProfileCell sim={sim} />
      {network !== undefined && <SimNetworkCell network={network} />}
      {/* Desktop: our device, then the detected modem below it. Mobile: one
          line, our device if assigned, otherwise the modem. */}
      <TableCell className="whitespace-nowrap" data-testid="sim-device">
        {sim.equipmentInstanceID ? (
          <button
            type="button"
            className="text-left hover:underline"
            onClick={() =>
              onOpenDevice(sim.equipmentInstanceID!, sim.deviceName ?? "Device")
            }
          >
            {sim.deviceName}
            {sim.slot ? (
              <span className="text-muted-foreground"> · Slot {sim.slot}</span>
            ) : null}
          </button>
        ) : (
          <span
            className={cn("text-muted-foreground", modem && "max-md:hidden")}
          >
            –
          </span>
        )}
        {modem && (
          <div
            className={cn(
              "text-xs text-muted-foreground",
              sim.equipmentInstanceID
                ? "max-md:hidden"
                : "max-md:text-sm max-md:text-foreground",
            )}
            title={sim.imei ? `Modem, IMEI ${sim.imei}` : undefined}
            data-testid="sim-modem"
          >
            {modem}
          </div>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right tabular-nums">
        {sim.source === "manual" ? "–" : formatDataMb(currentMonthUsageMb(sim))}
      </TableCell>
      <TableCell>
        {sim.source === "manual" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label="SIM actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEditManual(sim)}>
                <Pencil className="mr-2 h-4 w-4" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => onDeleteManual(sim.id)}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
}, simRowPropsEqual);

function copyIccid(iccid: string) {
  void navigator.clipboard
    ?.writeText(iccid)
    .then(() => toast.success("ICCID copied"));
}

function SimPagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  return (
    <Pagination className="mx-0 w-auto" data-testid="sim-pagination">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => onChange(page - 1)}
            aria-disabled={page === 1}
            className={
              page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"
            }
          />
        </PaginationItem>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
          <PaginationItem key={p}>
            <PaginationLink
              isActive={p === page}
              onClick={() => onChange(p)}
              className="cursor-pointer"
            >
              {p}
            </PaginationLink>
          </PaginationItem>
        ))}
        <PaginationItem>
          <PaginationNext
            onClick={() => onChange(page + 1)}
            aria-disabled={page === totalPages}
            className={
              page === totalPages
                ? "pointer-events-none opacity-50"
                : "cursor-pointer"
            }
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
